/**
 * MedicalPhysics - Motor de física específico para simulación médica
 * Gestiona fuerzas entre nodos clínicos con significado médico
 */

import * as THREE from 'three';
import { ClinicalNode } from '../medical/ClinicalNode';
import { MEDICAL_PHYSICS } from '../core/config';

// ═══════════════════════════════════════════════════════════════════════════════
// CLASE PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════════

export class MedicalPhysics {
  nodes: ClinicalNode[];
  gravityCenter: THREE.Vector3;

  // Fuerzas configurables
  attractionStrength: number;
  repulsionStrength: number;
  centerGravity: number;
  springStiffness: number;
  friction: number;

  // Hipótesis diagnóstica activa (set por setActiveHypothesis).
  // Mapea nodeId → delta [-100..+100] donde >0 = evidencia a favor, <0 = en contra.
  // Cuando está set, los nodos a favor se atraen al "anchor" y los contrarios se repelen.
  private hypothesisDeltas: Map<string, number> | null = null;
  private hypothesisAnchor: THREE.Vector3 = new THREE.Vector3();
  private hypothesisConfirmed: boolean = false;

  // Rampa temporal de la hipótesis para que el cambio no sea un salto brusco.
  // rampStart: momento del último setActiveHypothesis (fade in desde 0→1).
  // fadeOutStart: si >0, estamos apagando la hipótesis (1→0), luego se limpia.
  private hypothesisRampStart = 0;
  private hypothesisFadeOutStart = 0;
  private static readonly HYPOTHESIS_RAMP_MS = 500;

  constructor(nodes: ClinicalNode[], gravityCenter?: THREE.Vector3) {
    this.nodes = nodes;
    this.gravityCenter = gravityCenter || MEDICAL_PHYSICS.GRAVITY_CENTER.clone();

    // Inicializar con valores por defecto
    this.attractionStrength = MEDICAL_PHYSICS.ATTRACTION_STRENGTH;
    this.repulsionStrength = MEDICAL_PHYSICS.REPULSION_STRENGTH;
    this.centerGravity = MEDICAL_PHYSICS.CENTER_GRAVITY;
    this.springStiffness = MEDICAL_PHYSICS.SPRING_STIFFNESS;
    this.friction = MEDICAL_PHYSICS.FRICTION;
  }

  // ═════════════════════════════════════════════════════════════════════════════
  // APLICACIÓN DE FUERZAS PRINCIPAL
  // ═════════════════════════════════════════════════════════════════════════════

  applyForces(): void {
    this.applyNodeRepulsion();
    this.applyDiagnosticAttraction();
    this.applyContraindicationRepulsion();
    this.applyRelationshipSprings();
    this.applyUrgencyCentrifuge();
    this.applyTemporalDrag();
    this.applyCenterGravity();
    this.applyHypothesisField();
    this.applyBoundaryConstraints();
    this.applyIdleDecay();
    this.applyHoverFreeze();
  }

  /**
   * Cuando el sistema está cerca del reposo (poca energía cinética), aplica
   * una fricción adicional para que se asiente completamente en vez de
   * oscilar indefinidamente. Sin esto, los nodos flotan eternamente aun sin
   * cambios clínicos y cuesta apuntarles.
   */
  private applyIdleDecay(): void {
    const energy = this.getTotalEnergy();
    if (energy < 0.5) {
      const decay = 0.88;
      this.nodes.forEach(n => n.velocity.multiplyScalar(decay));
    }
  }

  /**
   * El nodo bajo el cursor se congela. El alumno puede apuntar con precisión
   * sin que el nodo "escape" por la física. Arrastrar con mouse también
   * setea draggedNodeId y respeta este freeze.
   */
  private applyHoverFreeze(): void {
    const hoveredId = this.hoveredNodeId;
    if (!hoveredId) return;
    const node = this.nodes.find(n => n.data.id === hoveredId);
    if (node) {
      node.velocity.set(0, 0, 0);
      node.acceleration?.set(0, 0, 0);
    }
  }

  /** Set externamente desde MouseController/state — evita import circular. */
  hoveredNodeId: string | null = null;

  // ═════════════════════════════════════════════════════════════════════════════
  // CAMPO DE HIPÓTESIS DIAGNÓSTICA
  // ═════════════════════════════════════════════════════════════════════════════

  /**
   * Activa el campo de hipótesis. Los deltas provienen de differential-data.ts
   * (findings: Record<nodeId, number>). Positivo = evidencia a favor; negativo = en contra.
   * El anchor se coloca por encima del centro de gravedad para que el grupo de
   * evidencia quede visualmente arriba y separado del ruido.
   */
  setActiveHypothesis(deltas: Map<string, number>, confirmed: boolean = false): void {
    this.hypothesisDeltas = deltas;
    this.hypothesisConfirmed = confirmed;
    this.hypothesisAnchor.copy(this.gravityCenter).add(new THREE.Vector3(0, 2.5, 0));
    this.hypothesisRampStart = performance.now();
    this.hypothesisFadeOutStart = 0;
  }

  clearActiveHypothesis(): void {
    // Si ya hay hipótesis activa, apagamos con fade; si no, corte directo.
    if (this.hypothesisDeltas && this.hypothesisDeltas.size > 0) {
      this.hypothesisFadeOutStart = performance.now();
    } else {
      this.hypothesisDeltas = null;
      this.hypothesisConfirmed = false;
    }
  }

  /**
   * Fuerza proporcional al delta: evidencia fuerte (|delta|>=20) se mueve
   * notoriamente; evidencia leve apenas. Escalamos por 0.02 para que
   * conviva con las otras fuerzas sin dominar el campo.
   */
  private applyHypothesisField(): void {
    if (!this.hypothesisDeltas || this.hypothesisDeltas.size === 0) return;

    const now = performance.now();
    const RAMP = MedicalPhysics.HYPOTHESIS_RAMP_MS;
    let rampFactor = Math.min(1, (now - this.hypothesisRampStart) / RAMP);

    if (this.hypothesisFadeOutStart > 0) {
      const fadeElapsed = now - this.hypothesisFadeOutStart;
      rampFactor = Math.max(0, 1 - fadeElapsed / RAMP);
      if (rampFactor === 0) {
        this.hypothesisDeltas = null;
        this.hypothesisConfirmed = false;
        this.hypothesisFadeOutStart = 0;
        return;
      }
    }

    const base = this.hypothesisConfirmed ? 0.035 : 0.02;
    const intensity = base * rampFactor;

    this.nodes.forEach(node => {
      const delta = this.hypothesisDeltas!.get(node.data.id);
      if (delta === undefined || delta === 0) return;

      const direction = this.hypothesisAnchor.clone().sub(node.position);
      const distance = direction.length();
      if (distance < 0.1) return;
      direction.normalize();

      // delta>0: atraer (direction hacia anchor). delta<0: repeler (invertir).
      const signed = delta * intensity;
      const force = direction.multiplyScalar(signed);
      node.applyForce(force);
    });
  }

  /**
   * Repulsión universal entre todos los nodos para evitar solapamiento
   */
  private applyNodeRepulsion(): void {
    for (let i = 0; i < this.nodes.length; i++) {
      for (let j = i + 1; j < this.nodes.length; j++) {
        const a = this.nodes[i];
        const b = this.nodes[j];
        const dir = a.position.clone().sub(b.position);
        const dist = dir.length();

        if (dist < MEDICAL_PHYSICS.MIN_DISTANCE * 3 && dist > 0.01) {
          dir.normalize();
          const strength = this.repulsionStrength * (1 / (dist * dist));
          a.applyForce(dir.clone().multiplyScalar(strength));
          b.applyForce(dir.clone().multiplyScalar(-strength));
        }
      }
    }
  }

  // ═════════════════════════════════════════════════════════════════════════════
  // 1. ATRACCIÓN DIAGNÓSTICA
  // ═════════════════════════════════════════════════════════════════════════════
  
  private applyDiagnosticAttraction(): void {
    // Los síntomas y signos son atraídos hacia diagnósticos que los explican
    // (vía relationships[] con type 'indicates' | 'causes').
    const evidenceNodes = this.nodes.filter(n =>
      n.data.type === 'symptom' ||
      n.data.type === 'vital_sign' ||
      n.data.type === 'laboratory'
    );
    
    evidenceNodes.forEach(evidence => {
      // Encontrar diagnósticos relacionados
      evidence.data.relationships.forEach(rel => {
        if (rel.type === 'indicates' || rel.type === 'causes') {
          const diagnosis = this.nodes.find(n => n.data.id === rel.targetId);
          if (diagnosis) {
            const direction = diagnosis.position.clone().sub(evidence.position);
            const distance = direction.length();
            
            if (distance > 0.1) {
              direction.normalize();
              // Fuerza proporcional a la fuerza de la relación
              const force = direction.multiplyScalar(
                this.attractionStrength * rel.strength * 0.5
              );
              evidence.applyForce(force);
            }
          }
        }
      });
    });
  }

  // ═════════════════════════════════════════════════════════════════════════════
  // 2. REPULSIÓN POR CONTRAINDICACIONES
  // ═════════════════════════════════════════════════════════════════════════════
  
  private applyContraindicationRepulsion(): void {
    // Tratamientos y diagnósticos que se contraindican se repelen
    
    this.nodes.forEach(nodeA => {
      nodeA.data.relationships.forEach(rel => {
        if (rel.type === 'contraindicates') {
          const nodeB = this.nodes.find(n => n.data.id === rel.targetId);
          if (nodeB) {
            const direction = nodeA.position.clone().sub(nodeB.position);
            const distance = direction.length();
            
            if (distance > 0.1 && distance < MEDICAL_PHYSICS.MAX_DISTANCE) {
              direction.normalize();
              // Repulsión fuerte para contraindicaciones
              const force = direction.multiplyScalar(
                this.repulsionStrength * 2 * rel.strength
              );
              nodeA.applyForce(force);
              nodeB.applyForce(force.multiplyScalar(-1));
            }
          }
        }
      });
    });
  }

  // ═════════════════════════════════════════════════════════════════════════════
  // 3. SPRINGS ENTRE NODOS RELACIONADOS
  // ═════════════════════════════════════════════════════════════════════════════
  
  private applyRelationshipSprings(): void {
    // Conexiones actúan como springs que mantienen distancia ideal
    
    this.nodes.forEach(node => {
      node.data.relationships.forEach(rel => {
        const otherNode = this.nodes.find(n => n.data.id === rel.targetId);
        if (!otherNode) return;
        
        // Evitar calcular dos veces
        if (node.data.id > otherNode.data.id) return;
        
        const direction = otherNode.position.clone().sub(node.position);
        const distance = direction.length();
        
        // Distancia ideal basada en tipo de relación
        let idealDistance = MEDICAL_PHYSICS.IDEAL_DISTANCE;
        
        switch (rel.type) {
          case 'causes':
            idealDistance = 1.5;
            break;
          case 'indicates':
            idealDistance = 2.0;
            break;
          case 'treats':
            idealDistance = 1.0;
            break;
          case 'associated-with':
            idealDistance = 2.5;
            break;
          case 'temporal-sequence':
            idealDistance = 3.0;
            break;
        }
        
        // Fuerza de spring: F = -k * (x - x0)
        const displacement = distance - idealDistance;
        direction.normalize();
        
        const springForce = direction.multiplyScalar(
          this.springStiffness * displacement * rel.strength
        );
        
        node.applyForce(springForce);
        otherNode.applyForce(springForce.multiplyScalar(-1));
      });
    });
  }

  // ═════════════════════════════════════════════════════════════════════════════
  // 4. FUERZA CENTRÍFUGA POR URGENCIA
  // ═════════════════════════════════════════════════════════════════════════════
  
  private applyUrgencyCentrifuge(): void {
    // Nodos más urgentes/críticos tienden a alejarse del centro
    // Esto los hace más visibles y accesibles
    
    this.nodes.forEach(node => {
      const urgency = node.data.physics.timeCriticality;
      if (urgency > 0.5) {
        const direction = node.position.clone().sub(this.gravityCenter);
        const distance = direction.length();
        
        if (distance > 0.1) {
          direction.normalize();
          // Fuerza proporcional a la urgencia
          const centrifugalForce = direction.multiplyScalar(
            urgency * 0.1
          );
          node.applyForce(centrifugalForce);
        }
      }
    });
  }

  // ═════════════════════════════════════════════════════════════════════════════
  // 5. ARRASTRE TEMPORAL
  // ═════════════════════════════════════════════════════════════════════════════
  
  private applyTemporalDrag(): void {
    // Nodos más antiguos se mueven más lentamente
    // Simula que datos históricos están "asentados"
    
    const now = Date.now();
    
    this.nodes.forEach(node => {
      const nodeTime = new Date(node.data.recordedDate).getTime();
      const hoursOld = (now - nodeTime) / (1000 * 60 * 60);
      
      if (hoursOld > 24) {
        // Aplicar más fricción a nodos viejos
        const dragFactor = Math.min(0.9, 0.5 + hoursOld * 0.01);
        node.velocity.multiplyScalar(dragFactor);
      }
    });
  }

  // ═════════════════════════════════════════════════════════════════════════════
  // 6. GRAVEDAD HACIA CENTRO
  // ═════════════════════════════════════════════════════════════════════════════
  
  private applyCenterGravity(): void {
    // Tirón suave hacia el centro para mantener el grafo cohesionado
    
    this.nodes.forEach(node => {
      const direction = this.gravityCenter.clone().sub(node.position);
      const distance = direction.length();
      
      // Gravedad más fuerte si está lejos
      const gravityStrength = this.centerGravity * (distance / 10);
      
      direction.normalize();
      const gravityForce = direction.multiplyScalar(gravityStrength);
      node.applyForce(gravityForce);
    });
  }

  // ═════════════════════════════════════════════════════════════════════════════
  // 7. CONDICIONES DE FRONTERA
  // ═════════════════════════════════════════════════════════════════════════════
  
  private applyBoundaryConstraints(): void {
    // Mantener nodos dentro de una esfera
    
    this.nodes.forEach(node => {
      const distance = node.position.distanceTo(this.gravityCenter);
      
      if (distance > MEDICAL_PHYSICS.BOUNDARY_RADIUS) {
        const direction = this.gravityCenter.clone().sub(node.position).normalize();
        const overshoot = distance - MEDICAL_PHYSICS.BOUNDARY_RADIUS;
        
        // Empujar de vuelta con fuerza proporcional a cuánto se pasó
        const boundaryForce = direction.multiplyScalar(overshoot * 0.5);
        node.applyForce(boundaryForce);
        
        // También reducir velocidad radial
        const radialVelocity = node.velocity.dot(direction.clone().multiplyScalar(-1));
        if (radialVelocity > 0) {
          node.velocity.multiplyScalar(0.8);
        }
      }
    });
  }

  // ═════════════════════════════════════════════════════════════════════════════
  // FUERZAS ADICIONALES
  // ═════════════════════════════════════════════════════════════════════════════
  
  applyMouseRepulsion(mousePosition: THREE.Vector3, strength: number = 1): void {
    // Repulsión del cursor/mano. Sólo afecta nodos visibles: los ocultos
    // por discovery mode deben conservar su layout radial original para
    // aparecer en su posición correcta al revelarse; si los empujamos
    // invisibles, "saltan" cuando recién aparezcan.
    this.nodes.forEach(node => {
      if (!node.visible) return;
      const direction = node.position.clone().sub(mousePosition);
      const distance = direction.length();

      if (distance < 2 && distance > 0.1) {
        direction.normalize();
        const repulsion = direction.multiplyScalar(strength / (distance * distance));
        node.applyForce(repulsion);
      }
    });
  }

  applyTargetPositionForces(): void {
    // Aplicar fuerzas hacia targetPosition si está definida
    
    this.nodes.forEach(node => {
      if (node.targetPosition) {
        const direction = node.targetPosition.clone().sub(node.position);
        const distance = direction.length();
        
        if (distance > 0.1) {
          direction.normalize();
          // Fuerza proporcional a la distancia (spring)
          const force = direction.multiplyScalar(distance * 0.1);
          node.applyForce(force);
        }
      }
    });
  }

  // ═════════════════════════════════════════════════════════════════════════════
  // COLISIONES
  // ═════════════════════════════════════════════════════════════════════════════
  
  checkCollisions(): MedicalCollision[] {
    const collisions: MedicalCollision[] = [];
    
    for (let i = 0; i < this.nodes.length; i++) {
      for (let j = i + 1; j < this.nodes.length; j++) {
        const nodeA = this.nodes[i];
        const nodeB = this.nodes[j];
        
        const distance = nodeA.position.distanceTo(nodeB.position);
        const minDistance = nodeA.getBoundingRadius() + nodeB.getBoundingRadius();
        
        if (distance < minDistance) {
          collisions.push({
            nodeA,
            nodeB,
            penetration: minDistance - distance,
            point: nodeA.position.clone().add(nodeB.position).multiplyScalar(0.5),
          });
        }
      }
    }
    
    return collisions;
  }

  resolveCollisions(collisions: MedicalCollision[]): void {
    collisions.forEach(collision => {
      const { nodeA, nodeB, penetration } = collision;
      
      // Separar nodos
      const direction = nodeA.position.clone().sub(nodeB.position).normalize();
      const separation = direction.multiplyScalar(penetration * 0.5);
      
      nodeA.position.add(separation);
      nodeB.position.sub(separation);
      
      // Aplicar impulso para separación
      const relativeVelocity = nodeA.velocity.clone().sub(nodeB.velocity);
      const separatingVelocity = relativeVelocity.dot(direction);
      
      if (separatingVelocity < 0) {
        const impulse = direction.multiplyScalar(-separatingVelocity * 0.5);
        nodeA.velocity.add(impulse);
        nodeB.velocity.sub(impulse);
      }
    });
  }

  // ═════════════════════════════════════════════════════════════════════════════
  // UTILIDADES
  // ═════════════════════════════════════════════════════════════════════════════
  
  setGravityCenter(center: THREE.Vector3): void {
    this.gravityCenter.copy(center);
  }

  dampenVelocities(factor: number = 0.9): void {
    this.nodes.forEach(node => {
      node.velocity.multiplyScalar(factor);
    });
  }

  getTotalEnergy(): number {
    // Energía cinética total del sistema
    return this.nodes.reduce((total, node) => {
      return total + 0.5 * node.mass * node.velocity.lengthSq();
    }, 0);
  }

  stabilize(maxIterations: number = 100): void {
    // Simular sin renderizado hasta estabilizar
    for (let i = 0; i < maxIterations; i++) {
      this.applyForces();
      
      const collisions = this.checkCollisions();
      this.resolveCollisions(collisions);
      
      // Verificar si está estable
      const energy = this.getTotalEnergy();
      if (energy < 0.01) break;
    }
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
// TIPOS
// ═══════════════════════════════════════════════════════════════════════════════

export interface MedicalCollision {
  nodeA: ClinicalNode;
  nodeB: ClinicalNode;
  penetration: number;
  point: THREE.Vector3;
}

export default MedicalPhysics;
